/**
 * Code pages of the ESC/POS renderer. A receipt printer prints single bytes; the page
 * chosen with `ESC t n` says which glyph each byte above 0x7F is. We support the three
 * pages every Epson-compatible printer has (PC437, PC858, WPC1252). None of them has
 * the Indian rupee sign, so by default `₹` is printed as `Rs`; a printer whose firmware
 * does carry it (a custom or user-defined glyph) can say where with `rupeeByte`.
 */

export type CodepageName = 'cp437' | 'cp858' | 'cp1252';

export interface Codepage {
  readonly name: CodepageName;
  /** The `n` of `ESC t n` (Epson numbering). */
  readonly escT: number;
  /** Unicode characters of bytes 0x80..0xFF, in order (128 entries). */
  readonly upper: string;
}

const CP437_UPPER =
  'ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ';

const CP858_UPPER =
  'ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø×ƒáíóúñÑªº¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ðÐÊËÈ€ÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµþÞÚÛÙýÝ¯´­±‗¾¶§÷¸°¨·¹³²■ ';

const CP1252_UPPER =
  '€\u0081‚ƒ„…†‡ˆ‰Š‹Œ\u008dŽ\u008f\u0090‘’“”•–—˜™š›œ\u009džŸ' +
  Array.from({ length: 96 }, (_, i) => String.fromCharCode(0xa0 + i)).join('');

export const CODEPAGES: Readonly<Record<CodepageName, Codepage>> = {
  cp437: { name: 'cp437', escT: 0, upper: CP437_UPPER },
  cp858: { name: 'cp858', escT: 19, upper: CP858_UPPER },
  cp1252: { name: 'cp1252', escT: 16, upper: CP1252_UPPER },
};

export const RUPEE_SIGN = '₹';

/** Punctuation that looks the same in ASCII. */
const LOOKALIKES: Readonly<Record<string, string>> = {
  '‘': "'",
  '’': "'",
  '“': '"',
  '”': '"',
  '–': '-',
  '—': '-',
  '•': '*',
  '…': '...',
  ' ': ' ',
  '€': 'EUR',
  '\t': ' ',
};

const reverseCache = new Map<CodepageName, Map<string, number>>();

function reverse(cp: Codepage): Map<string, number> {
  let m = reverseCache.get(cp.name);
  if (!m) {
    m = new Map();
    [...cp.upper].forEach((ch, i) => {
      if (!m?.has(ch)) m?.set(ch, 0x80 + i);
    });
    reverseCache.set(cp.name, m);
  }
  return m;
}

/**
 * Replace what the printer cannot show, before layout (so line widths stay right):
 * `₹` becomes `Rs` unless the printer has a rupee glyph (`rupeeByte`).
 */
export function substituteText(text: string, hasRupeeGlyph: boolean): string {
  return hasRupeeGlyph ? text : text.split(RUPEE_SIGN).join('Rs');
}

/** Encode text in a code page. Unknown characters lose their accents, else become `?`. */
export function encodeText(text: string, cp: Codepage, rupeeByte: number | null): number[] {
  const rev = reverse(cp);
  const out: number[] = [];
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 63;
    if (code >= 0x20 && code < 0x7f) {
      out.push(code);
      continue;
    }
    if (ch === RUPEE_SIGN) {
      if (rupeeByte !== null) out.push(rupeeByte);
      else out.push(0x52, 0x73); // "Rs" (layout normally replaced it already)
      continue;
    }
    const direct = rev.get(ch);
    if (direct !== undefined) {
      out.push(direct);
      continue;
    }
    const like = LOOKALIKES[ch];
    if (like !== undefined) {
      for (const c of like) out.push(c.charCodeAt(0));
      continue;
    }
    const base = ch.normalize('NFD').replace(/[̀-ͯ]/g, '');
    if (base !== ch && base.length > 0 && [...base].every((c) => c.charCodeAt(0) < 0x7f)) {
      for (const c of base) out.push(c.charCodeAt(0) >= 0x20 ? c.charCodeAt(0) : 63);
      continue;
    }
    out.push(63);
  }
  return out;
}
